import { Plan } from '../models/plans';

/** The points that each card showed before the planCardPoints flag. */
export const FIXED_CARD_POINTS: ReadonlyArray<string> = [
  'No lock in contract',
  'Unlimited data',
  'Equal up and download speeds',
];

interface Speeds {
  down: number;
  up: number;
}

/** The advertised speeds of a plan, as the card title shows them. */
export const planSpeedsOf = (plan: Plan): Speeds => ({
  down: plan.download || plan.speed,
  up: plan.upload || plan.speed,
});

/**
 * The typical evening speeds of a plan. The backend sends the pair for the
 * access technology of the building in evening_download / evening_upload.
 * If a value is not set, the advertised speed is used for it.
 */
export const eveningSpeedsOf = (plan: Plan): Speeds => {
  const advertised = planSpeedsOf(plan);
  return {
    down: plan.evening_download || advertised.down,
    up: plan.evening_upload || advertised.up,
  };
};

/**
 * The last point on the card, with the planCardPoints flag on. The card
 * shows the mark in superscript when "superscript" is true.
 */
export interface SpeedPoint extends Speeds {
  label: string;
  mark: string;
  superscript: boolean;
}

/**
 * The typical evening speed, or the maximum theoretical speed when the
 * evening speeds are not set or are the same as the advertised speeds.
 */
export const speedPoint = (plan: Plan): SpeedPoint => {
  const advertised = planSpeedsOf(plan);
  const evening = eveningSpeedsOf(plan);
  if (evening.down === advertised.down && evening.up === advertised.up) {
    return {
      label: 'Max Theoretical speed',
      mark: '#',
      superscript: true,
      ...advertised,
    };
  }
  return {
    label: 'Typical evening speed',
    mark: '*',
    superscript: false,
    ...evening,
  };
};

/** The speed point as plain text, for tests and for screen readers. */
export const speedPointText = (point: SpeedPoint): string => (
  `${point.label}${point.mark} ${point.down}/${point.up}Mbps`
);

/**
 * The text points with a tick on the plan card, in sequence.
 *
 * With the planCardPoints flag off (the default), it is the three fixed
 * points that each card always showed.
 *
 * With the flag on, it is the points of the plan from the marketing portal.
 * The card then adds speedPoint as the last point. A plan with no stored
 * points gets no text points: the backend command backfillCardPoints gives
 * the plans their points before the flag goes on. Empty points are not
 * shown.
 */
export const cardPoints = (plan: Plan, planCardPoints = false): string[] => {
  if (!planCardPoints) return [...FIXED_CARD_POINTS];
  return (plan.card_points ?? []).filter((point) => point.trim() !== '');
};
